import { Dropdown, MenuProps } from "antd";
import { FaRegFolder } from "react-icons/fa";
import { RxDashboard, RxGroup } from 'react-icons/rx';
import { buildCreateItems, createDialogFor, deleteDialogFor, renameDialogFor, TreeMenuConfig } from "src/component/dashboardTree/TreeContextMenu";
import { dashboardDomain } from "src/component/dashboardTree/dashboardDomain";
import { CreatableType, HierarchyAccessors, HierarchyAction } from "src/component/dashboardTree/hierarchyTypes";
import { DashboardMenuNode, menuDataNodeAccessors } from "src/component/dashboardTree/menuDataNodeAccessors";
import { MenuNode } from "src/model/MenuNode";
import { GLOB } from "src/util/Glob";
import { Log } from '../../../service/log.service';
import '../DashboardPart.less';
import { makeDashboardDefault } from "../dashboardService";

enum Context {
  FOLDER = 'folder', DASHBOARD = 'dashboard', DEFAULT = 'default', GROUP = 'group', RENAME = 'rename',
  DELETE = 'delete'
}

interface ContextState {
  options: MenuDataNode[];
};

export type MenuDataNode = DashboardMenuNode;

export class DashboardContextMenu {

  static readonly ROOT = 'root';
  static readonly NO_GROUP = 'noGroup';

  private readonly noGroup: boolean;
  private readonly icons: boolean;

  constructor(private readonly getState: () => Readonly<ContextState>,
    private readonly setState: (state: ContextState) => void,
    private readonly isDashboard: (item: MenuDataNode) => boolean,
    private readonly isGroup?: (item: MenuDataNode) => boolean,
    private readonly onDelete?: (item: MenuDataNode) => void,
    private readonly onCreateGroup?: () => void, icons = false) {
    this.noGroup = !isGroup;
    this.icons = icons;
  }

  static equals(first: MenuDataNode, second: MenuDataNode) {
    if (first === second) {
      return true;
    } else if (first && second) {
      return first.itemId && first.itemId === second.itemId || first.key && first.key === second.key;
    } else if (!first) {
      return !second;
    }
    return false;
  }

  createLabel(item: MenuDataNode) {
    return item.divider ? item.title : <Dropdown menu={this.contextMenu(item)} trigger={['contextMenu']}>
      {this.getIconTitle(item)}
    </Dropdown>;
  }

  getIconTitle(item: MenuDataNode) {
    return <div className="xm-dashboard-dropdown" title={item.disabledReason}>
      {this.icons ? item.dashboard ? <RxDashboard title="Dashboard" />
        : item.group ? <RxGroup title="Group" />
          : <FaRegFolder title="Folder" />
        : null}
      <span>{item.titleText ? item.titleText : item.label}</span>
    </div>;
  }

  private readonly acc: HierarchyAccessors<MenuDataNode> = {
    ...menuDataNodeAccessors,
    isDashboard: item => Boolean(this.isDashboard(item)),
    isGroup: item => Boolean(this.isGroup?.(item)),
    makeChild: (created, type, parent, childUrl) => {
      const child = menuDataNodeAccessors.makeChild(created, type, parent, childUrl);
      if (type === HierarchyAction.DASHBOARD && this.noGroup) { child.isLeaf = true; child.children = undefined; }
      return child;
    },
    insertChild: (parent, child) => {
      if (child.group && this.noGroup) return;
      if (parent.children?.[0]?.itemId === DashboardContextMenu.NO_GROUP) parent.children = [];
      menuDataNodeAccessors.insertChild(parent, child);
    },
  };

  private cfg(): TreeMenuConfig<MenuDataNode> {
    return {
      acc: this.acc,
      domain: dashboardDomain,
      ctx: { getNodes: () => this.getState().options, setNodes: options => this.setState({ options }) },
      relabel: (live, label) => { live.titleText = label; live.title = this.createLabel(live); },
      decorateChild: child => { child.title = this.createLabel(child); },
      onCreated: (_child, type) => { if (type === HierarchyAction.GROUP) this.onCreateGroup?.(); },
      onDeleted: node => this.onDelete?.(node),
    };
  }

  private removeStyle(nodes: MenuDataNode[]) {
    if (!nodes) return;
    for (const node of nodes) {
      if (node.styleClass === 'star')
        node.styleClass = null;
      this.removeStyle(node.children);
    }
  }

  private contextMenu(item: MenuDataNode): MenuProps {
    const isDashboard = this.isDashboard(item);
    const isGroup = this.isGroup?.(item);
    const isDefault = (item.styleClass === 'star');
    const belongsToGroup = (MenuNode.getGroupId(item) != null);
    const { readonly } = GLOB.userInfo;

    const type = isDashboard
      ? Context.DASHBOARD
      : (isGroup
        ? Context.GROUP
        : Context.FOLDER);

    const cfg = this.cfg();
    const createItems = buildCreateItems(item, cfg);
    // Insert DEFAULT between Create dashboard (index 1) and Create group (index 2).
    createItems.splice(2, 0, {
      key: Context.DEFAULT, disabled: !isDashboard || isDefault, label: 'Make default',
      title: 'Display this dashboard in initial navigation.'
    });
    createItems.push(
      { key: Context.RENAME, disabled: (readonly && belongsToGroup) || (item.itemId === DashboardContextMenu.ROOT) || !!item.group_id, label: 'Rename' },
      { key: Context.DELETE, disabled: (readonly && belongsToGroup) || (item.itemId === DashboardContextMenu.ROOT) || !!item.group_id, label: 'Delete' }
    );
    return {
      onClick: info => {
        info.domEvent.preventDefault();
        info.domEvent.stopPropagation();
        switch (info.key) {
          case Context.FOLDER:
          case Context.DASHBOARD:
          case Context.GROUP:
            createDialogFor(item, info.key as unknown as CreatableType, cfg);
            break;
          case Context.DEFAULT:
            void makeDashboardDefault(item).then(() => {
              Log.success(item.titleText + ' is now default dashboard.');
              const state = this.getState().options;
              this.removeStyle(state);
              item.styleClass = 'star';
              this.setState({ options: state });
            });
            break;
          case Context.RENAME:
            renameDialogFor(item, type, cfg);
            break;
          case Context.DELETE:
            deleteDialogFor(item, type, cfg);
            break;
          default:
            Log.warn('Unknown context menu ' + info.key);
        }
      },
      items: [
        {
          key: 'groupMenu',
          label: `${type[0].toUpperCase()}${type.substring(1).toLowerCase()} ${item.titleText}`,
          children: createItems
        }
      ]
    };
  }
}
